import React from 'react';
import { Typography, Card, Row, Col } from 'antd';

const { Title, Paragraph, Text } = Typography;

const sectionStyle = { marginBottom: 24 };
const subHeadingStyle = { fontSize: 18, fontWeight: 600, marginTop: 16, marginBottom: 8 };

const Privacy = () => (
  <div style={{ padding: '40px 24px' }}>
    <Row justify="center">
      <Col xs={24} sm={20} md={18} lg={14}>
        <Card className="rounded-3xl shadow-lg" style={{ border: 'none' }}>
          <Title level={1}>Privacy Policy</Title>
          <Paragraph>
            <Text type="secondary">Last updated: September 26, 2026</Text>
          </Paragraph>

          <div style={sectionStyle}>
            <Paragraph>
              Current Care ("we", "our", or "us") operates the Current Care mobile application and
              website (currentcarenpl.com). This Privacy Policy informs you of our policies regarding
              the collection, use, and disclosure of personal information when you use our Service.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>1. Information We Collect</Title>
            <Text style={subHeadingStyle} strong>Personal Information</Text>
            <Paragraph>When you register an account, we may collect:</Paragraph>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Delivery addresses</li>
              <li>Profile image</li>
            </ul>

            <Text style={subHeadingStyle} strong>Payment Information</Text>
            <Paragraph>
              We process payments through third-party payment processors (eSewa, Khalti, credit/debit
              cards). We do not store your full payment card details on our servers.
            </Paragraph>

            <Text style={subHeadingStyle} strong>Usage Data</Text>
            <Paragraph>We automatically collect information including:</Paragraph>
            <ul>
              <li>Device type and operating system</li>
              <li>IP address and browser type</li>
              <li>Pages visited and time spent on the app</li>
              <li>GPS location (only when you explicitly allow location tracking for delivery)</li>
            </ul>

            <Text style={subHeadingStyle} strong>Camera, Microphone and Media</Text>
            <Paragraph>
              We access your camera or photo library only when you explicitly use a related
              feature: uploading product images, profile pictures, QR payment screenshots, or
              images/videos in chat. We access your microphone only when you record a voice
              message in chat. Each permission is requested at the moment you use the feature, for
              that specific purpose only, and never in the background.
            </Paragraph>

            <Text style={subHeadingStyle} strong>Chat Media and Messages</Text>
            <Paragraph>
              Messages and media you send in chat (text, images, videos, voice messages, and
              shared locations) are transmitted to our servers so the recipient can view them, and
              are retained as described in this Policy.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>2. How We Use Your Information</Title>
            <Paragraph>We use the collected information to:</Paragraph>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information (order confirmations, invoices)</li>
              <li>Deliver products to your specified addresses</li>
              <li>Send promotional communications (only with your consent)</li>
              <li>Notify you about changes to our services</li>
              <li>Provide customer support</li>
              <li>Ensure the security of our platform</li>
              <li>Comply with legal obligations</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>3. Data Sharing</Title>
            <Paragraph>We may share your information with:</Paragraph>
            <ul>
              <li>
                <strong>Service Providers:</strong> Delivery partners receive your name, phone number,
                and delivery address to fulfill orders.
              </li>
              <li>
                <strong>Payment Processors:</strong> eSewa, Khalti, and card processors handle your
                payment data under their own privacy policies.
              </li>
              <li>
                <strong>Analytics Providers:</strong> Firebase Analytics helps us understand app usage
                patterns.
              </li>
              <li>
                <strong>Legal Authorities:</strong> When required by law or to protect our rights.
              </li>
            </ul>
            <Paragraph>
              We do not sell your personal information to third parties.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>4. Data Security</Title>
            <Paragraph>
              We implement industry-standard security measures including encrypted data transmission
              (HTTPS/TLS), secure token-based authentication, encrypted local storage using Flutter
              Secure Storage, and regular security audits. However, no method of electronic
              transmission or storage is 100% secure.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>5. Data Retention and Deletion</Title>
            <Paragraph>
              We retain your personal information for as long as your account is active or as
              needed to provide services. You can delete your account at any time from the App
              (<strong>Settings &rarr; Delete Account</strong>) or by using the{" "}
              <a href="/account-deletion">account deletion form</a> on this website. When you
              request deletion, we delete your account and associated personal data (including
              profile, chat history, and uploaded content), except where we need to retain certain
              information for legal or legitimate business purposes (such as transaction and tax
              records). Providers and delivery personnel with order history may require assisted
              deletion by contacting support at{" "}
              <a href="mailto:support@currentcarenpl.com">support@currentcarenpl.com</a>.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>6. Your Rights</Title>
            <Paragraph>You have the right to:</Paragraph>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your personal data</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent for location tracking at any time</li>
              <li>Request a copy of your data in a portable format</li>
            </ul>
            <Paragraph>
              To exercise these rights, contact us at{" "}
              <a href="mailto:support@currentcarenpl.com">support@currentcarenpl.com</a>.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>7. Children's Privacy</Title>
            <Paragraph>
              Our Service is not intended for anyone under the age of 16. We do not knowingly collect
              personal information from children under 16. If we discover that a child under 16 has
              provided us with personal information, we will immediately delete it.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>8. Third-Party Services</Title>
            <Paragraph>Our app uses the following third-party services:</Paragraph>
            <ul>
              <li>
                <strong>Firebase (Google):</strong> Authentication, analytics, cloud messaging
                <br />
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <strong>Google Sign-In:</strong> Account authentication
                <br />
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <strong>Apple Sign-In:</strong> Account authentication
                <br />
                <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer">
                  Apple Privacy Policy
                </a>
              </li>
              <li>
                <strong>Khalti:</strong> Digital wallet payments
                <br />
                <a href="https://khalti.com/privacy-policy/" target="_blank" rel="noreferrer">
                  Khalti Privacy Policy
                </a>
              </li>
              <li>
                <strong>Google Maps:</strong> Location services for delivery tracking
                <br />
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                  Google Privacy Policy
                </a>
              </li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>9. Changes to This Policy</Title>
            <Paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
              You are advised to review this Privacy Policy periodically for any changes.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>10. Contact Us</Title>
            <Paragraph>If you have any questions about this Privacy Policy, please contact us:</Paragraph>
            <ul>
              <li>Email: <a href="mailto:support@currentcarenpl.com">support@currentcarenpl.com</a></li>
              <li>Website: currentcarenpl.com/contact</li>
              <li>Address: Nepal</li>
            </ul>
          </div>
        </Card>
      </Col>
    </Row>
  </div>
);

export default Privacy;
